import { ConfigError, formatIssues } from './errors'
import { ITEM_IDS, itemsFileSchema } from './items.schema'
import type { ItemsFile } from './items.schema'
import { msToTicks, perSecToPerTick } from './ticks'

/**
 * Validated `items.json` (T1.3): durations in ticks, speeds in px/tick, sizes in px.
 * `weightsByRank[rank - 1][k]` is the weight of `ITEM_IDS[k]` for that live rank.
 */
export interface ItemBalance {
  readonly itemCooldownTicks: number
  readonly crateRespawnTicks: number
  readonly shieldPopInvulnTicks: number
  readonly crateSizePx: number
  readonly coinSizePx: number
  readonly boostJet: { readonly speedPct: number; readonly durationTicks: number }
  readonly shieldBubble: { readonly durationTicks: number }
  readonly tripWire: {
    readonly stunTicks: number
    readonly ownerGraceTicks: number
    readonly lifetimeTicks: number
    readonly dropBehindPx: number
    readonly widthPx: number
    readonly heightPx: number
  }
  readonly spikeBall: {
    readonly stunTicks: number
    readonly speedPxPerTick: number
    readonly lifetimeTicks: number
    readonly sizePx: number
  }
  readonly homingDrone: {
    readonly stunTicks: number
    readonly speedPxPerTick: number
    readonly verticalSpeedPxPerTick: number
    readonly lockPx: number
    readonly lifetimeTicks: number
    readonly sizePx: number
    readonly aimPct: number
  }
  readonly oilSlick: {
    readonly skidTicks: number
    readonly skidSpeedPct: number
    readonly ownerGraceTicks: number
    readonly lifetimeTicks: number
    readonly dropBehindPx: number
    readonly widthPx: number
    readonly heightPx: number
  }
  readonly leaderMissile: {
    readonly stunTicks: number
    readonly splashStunTicks: number
    readonly splashRadiusPx: number
    readonly speedPxPerTick: number
    readonly lifetimeTicks: number
    readonly sizePx: number
  }
  readonly magnet: { readonly durationTicks: number; readonly radiusPx: number }
  readonly weightsByRank: readonly (readonly number[])[]
}

export function loadItems(raw: unknown): ItemBalance {
  const parsed = itemsFileSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ConfigError(`Invalid config\n${formatIssues('items', parsed.error)}`)
  }
  const f = parsed.data
  const tw = f.trip_wire
  const sb = f.spike_ball
  const hd = f.homing_drone
  return Object.freeze({
    itemCooldownTicks: msToTicks(f.itemCooldownMs),
    crateRespawnTicks: msToTicks(f.crateRespawnMs),
    shieldPopInvulnTicks: msToTicks(f.shieldPopInvulnMs),
    crateSizePx: f.crateSizePx,
    coinSizePx: f.coinSizePx,
    boostJet: Object.freeze({
      speedPct: f.boost_jet.speedPct,
      durationTicks: msToTicks(f.boost_jet.durationMs),
    }),
    shieldBubble: Object.freeze({ durationTicks: msToTicks(f.shield_bubble.durationMs) }),
    tripWire: Object.freeze({
      stunTicks: msToTicks(tw.stunMs),
      ownerGraceTicks: msToTicks(tw.ownerGraceMs),
      lifetimeTicks: msToTicks(tw.lifetimeMs),
      dropBehindPx: tw.dropBehindPx,
      widthPx: tw.widthPx,
      heightPx: tw.heightPx,
    }),
    spikeBall: Object.freeze({
      stunTicks: msToTicks(sb.stunMs),
      speedPxPerTick: perSecToPerTick(sb.speedPxPerSec),
      lifetimeTicks: msToTicks(sb.lifetimeMs),
      sizePx: sb.sizePx,
    }),
    homingDrone: Object.freeze({
      stunTicks: msToTicks(hd.stunMs),
      speedPxPerTick: perSecToPerTick(hd.speedPxPerSec),
      verticalSpeedPxPerTick: perSecToPerTick(hd.verticalSpeedPxPerSec),
      lockPx: hd.lockPx,
      lifetimeTicks: msToTicks(hd.lifetimeMs),
      sizePx: hd.sizePx,
      aimPct: hd.aimPct,
    }),
    oilSlick: Object.freeze({
      skidTicks: msToTicks(f.oil_slick.skidMs),
      skidSpeedPct: f.oil_slick.skidSpeedPct,
      ownerGraceTicks: msToTicks(f.oil_slick.ownerGraceMs),
      lifetimeTicks: msToTicks(f.oil_slick.lifetimeMs),
      dropBehindPx: f.oil_slick.dropBehindPx,
      widthPx: f.oil_slick.widthPx,
      heightPx: f.oil_slick.heightPx,
    }),
    leaderMissile: Object.freeze({
      stunTicks: msToTicks(f.leader_missile.stunMs),
      splashStunTicks: msToTicks(f.leader_missile.splashStunMs),
      splashRadiusPx: f.leader_missile.splashRadiusPx,
      speedPxPerTick: perSecToPerTick(f.leader_missile.speedPxPerSec),
      lifetimeTicks: msToTicks(f.leader_missile.lifetimeMs),
      sizePx: f.leader_missile.sizePx,
    }),
    magnet: Object.freeze({
      durationTicks: msToTicks(f.magnet.durationMs),
      radiusPx: f.magnet.radiusPx,
    }),
    weightsByRank: Object.freeze(weightRows(f)),
  })
}

/** Rank rows in rank order, each row in `ITEM_IDS` order. */
function weightRows(f: ItemsFile): readonly (readonly number[])[] {
  const rows = [
    f.weightsByRank['1'],
    f.weightsByRank['2'],
    f.weightsByRank['3'],
    f.weightsByRank['4'],
  ]
  return rows.map((row) => Object.freeze(ITEM_IDS.map((id) => row[id])))
}
